import React, { useMemo, useState } from 'react';
import {
    KatLayout,
    KatProgress,
} from '@amzn/katal-react';

// Core hooks
import {
    getGSLADataForWarehouseAsMulti,
    getGSLARecommendationsForWarehouse,
    isRecommendationFresh,
    useFCMetadata,
} from 'src/core/hooks/useGranularSLA';
import { useURLSiteParam } from 'src/core/hooks/UseURLSiteParam';
import { useUser } from 'src/utils/UserContext';
import { getValidWHTsForCountry } from 'src/types/WarehouseHandlingTier';

// Reuse the existing hierarchical table adapter
import { GSLAHierarchicalTableAdapter } from '../../components/hierarchicalTable/GSLAHierarchicalTableAdapter';

// Types
import { GSLADefinitionsByMutipleWarehouses, GSLARecommendationsByMutipleWarehouses } from 'src/features/types/gslaApi';

// Styles
import '../../styles/granularSLA/productionCapability.scss';

/**
 * ProductionCapabilityManagerWithHierarchy replaces the original ProductionCapabilityManager
 * with a new implementation that uses the hierarchical table to display GSLA data
 * including handling tiers.
 */
const ProductionCapabilityManagerWithHierarchy = () => {
    // User context for permissions
    const { user } = useUser();

    // Check if user has edit permissions
    const hasEditPermission = user?.isAuthorizedForSLAOverrides === true || user?.isDevAdmin === true;

    // State for view-only mode - default to false for users with permissions
    const [viewOnlyMode, setViewOnlyMode] = useState<boolean>(false);

    // State for success notification
    const [showSuccessNotification, setShowSuccessNotification] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    // Get the site ID from URL
    const siteId = useURLSiteParam();

    // Fetch FC metadata for country-based WHT filtering
    const {
        data: fcMetadata,
        isLoading: isFCMetadataLoading,
    } = useFCMetadata(siteId || '');

    // Compute available tiers based on country code
    const availableTiers = getValidWHTsForCountry(fcMetadata?.countryCode);

    // Fetch GSLA data using the single-warehouse endpoint (has PC expansion logic)
    // Returns data in multi-warehouse format for GSLAHierarchicalTableAdapter
    const {
        data: gslaData,
        isLoading: isGSLADataLoading,
        refetch: refetchGSLA,
    } = getGSLADataForWarehouseAsMulti(siteId || '', !viewOnlyMode);

    // Fetch Default_Network_PC from the DEFAULT warehouse (not included in FC-specific response)
    const {
        data: defaultWarehouseData,
        isLoading: isDefaultWarehouseLoading,
        refetch: refetchDefault,
    } = getGSLADataForWarehouseAsMulti('DEFAULT', !viewOnlyMode);

    // Merge DEFAULT warehouse data into the site data so Default_Network_PC is visible
    const mergedGslaData: GSLADefinitionsByMutipleWarehouses | undefined = useMemo(() => {
        if (!gslaData) return undefined;
        if (!defaultWarehouseData) return gslaData;

        return {
            warehouseToGSLAMap: {
                ...gslaData.warehouseToGSLAMap,
                ...defaultWarehouseData.warehouseToGSLAMap,
            },
        };
    }, [gslaData, defaultWarehouseData]);

    // Fetch GSLA recommendations (single-warehouse, wrapped to multi-warehouse format)
    const {
        data: gslaRecommendationsRaw,
        isLoading: isGSLARecommendationsLoading,
    } = getGSLARecommendationsForWarehouse(
        siteId || '',
        { enabled: !!(user?.isAuthorizedForSLAOverrides || user?.isDevAdmin) },
    );

    // Wrap single-warehouse recommendations into multi-warehouse format for the adapter
    // Filter out stale recommendations (consistent with adaptGSLARecommendationsResponse)
    const gslaRecommendations: GSLARecommendationsByMutipleWarehouses | undefined =
        gslaRecommendationsRaw && siteId
            ? {
                warehouseToGSLARecommendationMap: {
                    [siteId]: gslaRecommendationsRaw.pcToGslaList.filter(
                        rec => isRecommendationFresh(rec.publishDateEpochMillis),
                    ),
                },
            }
            : undefined;

    // Handle successful save
    const handleSaveSuccess = (message?: string) => {
        // Refetch data to ensure we have the latest values
        refetchGSLA();
        refetchDefault();

        // Show success notification
        setSuccessMessage(message || 'GSLA values published successfully!');
        setShowSuccessNotification(true);

        // Auto-hide notification after 5 seconds
        setTimeout(() => {
            setShowSuccessNotification(false);
        }, 5000);
    };

    // Function to toggle view-only mode
    const toggleViewMode = () => {
        setViewOnlyMode(prev => !prev);
    };

    // Determine if user can edit based on permissions and view mode
    const userCanEdit = hasEditPermission && !viewOnlyMode;

    // Show loading state
    if (isGSLADataLoading || isDefaultWarehouseLoading || isGSLARecommendationsLoading || isFCMetadataLoading) {
        return (
            <div>
                GSLA Data is loading please wait a few seconds. The page will automatically load when it is ready.
                <KatProgress indeterminate></KatProgress>
            </div>
        );
    }

    // Main view for all users
    return (
        <React.Fragment>
            <KatLayout>
                <div slot='main'>
                    <h3>Granular SLA Data for: {siteId}</h3>

                    {/* Success Notification */}
                    {showSuccessNotification && (
                        <div style={{
                            marginBottom: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#d4edda',
                            border: '1px solid #c3e6cb',
                            borderRadius: '4px',
                            color: '#155724',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <span style={{ fontWeight: 500 }}>{successMessage}</span>
                            <button
                                onClick={() => setShowSuccessNotification(false)}
                                style={{
                                    marginLeft: '16px',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    color: '#155724',
                                    padding: '0 4px',
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    {/* Only show toggle if user has edit permissions */}
                    {hasEditPermission && (
                        <div style={{
                            padding: '16px',
                            backgroundColor: '#f5f5f5',
                            borderBottom: '1px solid #ddd',
                            marginBottom: '16px',
                        }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={viewOnlyMode}
                                    onChange={toggleViewMode}
                                    style={{ marginRight: '8px' }}
                                />
                                <span style={{ fontWeight: 500 }}>View-Only Mode</span>
                            </label>
                        </div>
                    )}

                    <GSLAHierarchicalTableAdapter
                        warehouseId={siteId || ''}
                        gslaData={mergedGslaData}
                        gslaRecommendations={gslaRecommendations}
                        isLoading={isGSLADataLoading || isDefaultWarehouseLoading || isGSLARecommendationsLoading}
                        userCanEdit={userCanEdit}
                        onSaveSuccess={handleSaveSuccess}
                        availableTiers={availableTiers}
                    />
                </div>
            </KatLayout>
        </React.Fragment>
    );
};

export default ProductionCapabilityManagerWithHierarchy;
